import { useAppStore } from '@store'
import type { AnyID, Item, LinkedItem } from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isLinkedSon from '@utils/sceneObjects/assertions/isLinkedSon'
import { cloneLinkedItemTexturesForInheritance } from '@utils/sceneObjects/getExistingTexturesFromScene'
import { getRelativeMatrix } from '@utils/sceneObjects/items/getRelativeMatrix'
import { composeStraightModuleAtRowExterior } from '@utils/modularSnap/rowSnapGuards'
import { Matrix4 } from 'three'

const isArmCatalog = (catalogID: unknown) => /^BR\d+(-\d+)?$/i.test(String(catalogID || ''))

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const cloneMorphs = (morphs: Item['morphs'] | undefined) =>
  morphs?.map((morph) => ({
    ...morph,
    default: morph.default,
    _default: morph._default ?? morph.default,
  })) ?? []

/** Resuelve la unidad linkedItem seleccionada (padre si la selección es un hijo). */
export function resolveSelectedLinkedUnit(
  items: Map<AnyID, any>,
  selected: Iterable<string | AnyID>,
  currentObjectID?: AnyID | string | '',
): LinkedItem | null {
  const candidateIds = [
    ...(currentObjectID ? [String(currentObjectID)] : []),
    ...Array.from(selected, String),
  ]

  for (const id of candidateIds) {
    const item = items.get(id as AnyID)
    if (!item) continue

    if (isLinkedItem(item) && !isArmCatalog(item.catalogID) && item.universal === false) {
      return item
    }

    if (isLinkedSon(item) && item.firstParentID) {
      const parent = items.get(item.firstParentID as AnyID)
      if (
        isLinkedItem(parent) &&
        !isArmCatalog(parent.catalogID) &&
        parent.universal === false
      ) {
        return parent
      }
    }
  }

  return null
}

const buildChildFinishPatch = (child: any, duplicatedParent: LinkedItem, newParentId: AnyID) => ({
  textures: Array.isArray(child.textures) ? [...child.textures] : child.textures,
  zones: child.zones ? { ...child.zones } : child.zones,
  idUnidades: child.idUnidades,
  x: child.x,
  y: child.y,
  z: child.z,
  width: duplicatedParent.width,
  height: duplicatedParent.height,
  thickness: duplicatedParent.thickness,
  morphs: cloneMorphs(child.morphs),
  glbSize: child.glbSize,
  referenciaBlender: child.referenciaBlender,
  catalogID: child.catalogID,
  linkedID: child.linkedID,
  description: child.description,
  visualOrder: child.visualOrder,
  quantity: child.quantity,
  matrix: child.matrix,
  glbPath: child.glbPath,
  universal: child.universal,
  depth: child.depth,
  parentOption: child.parentOption,
  option: child.option,
  handPosition: child.handPosition,
  hand: child.hand,
  typeOfSon: child.typeOfSon,
  isUnlinkable: child.isUnlinkable,
  firstParentID: newParentId,
  idElementosEnlazados: child.idElementosEnlazados,
  code: child.code,
  long: child.long,
  referencia: child.referencia,
  increment: child.increment,
  paginaPdf: child.paginaPdf,
  tipoNormalizado: child.tipoNormalizado,
  tipoUnidNorm: child.tipoUnidNorm,
  url: child.url,
})

/**
 * Duplica la unidad linkedItem seleccionada y la coloca siempre en el extremo
 * derecho de la fila, conservando acabados (texturas/zonas) del padre e hijos.
 */
export async function duplicateSelectedLinkedUnitAtRightEnd(): Promise<AnyID | null> {
  const store = useAppStore.getState()
  const selected = store.layers[store.currentLayer]?.selected
  if (!selected?.size && !store.currentObjectID) return null

  const source = resolveSelectedLinkedUnit(store.items, selected || [], store.currentObjectID)
  if (!source?.matrix || (source as any).isBlocked) return null

  const sourceChildrenIds =
    source.childrenOrder?.map((el) => el.split('!')[1] as AnyID).filter(Boolean) ?? []

  const drawWidth = Number(source.width || 0)
  const exterior =
    drawWidth > 0
      ? composeStraightModuleAtRowExterior(
          store.items as Map<string, any>,
          source as any,
          drawWidth,
          'right',
        )
      : null

  const anchorId = (exterior?.endUnitId || String(source.id)) as AnyID
  const relativeMatrix: Matrix4 =
    exterior?.matrix ?? getRelativeMatrix(source.matrix, drawWidth || source.width)

  const pos = toVector3()
  relativeMatrix.decompose(pos, toQuaternion(), toVector3())

  store.unselectAll()

  const newId = await store.drawItem(
    String(source.id),
    String(source.catalogID),
    source.image,
    true,
    relativeMatrix,
    undefined,
    undefined,
    false,
    {
      useChainedDrawMatrix: true,
      chainedDrawSnapSide: 'right',
      preDrawSelectedAnchorId: anchorId,
      inheritTexturesFrom: cloneLinkedItemTexturesForInheritance(source),
    },
  )

  if (!newId) return null

  // Esperar a que el post-procesado deje hijos (asiento/cojines) en escena.
  await wait(120)

  const duplicated = useAppStore.getState().items.get(newId)
  if (!isLinkedItem(duplicated)) return newId

  store.editItem(newId, {
    textures: Array.isArray(source.textures) ? [...source.textures] : source.textures,
    zones: source.zones ? { ...source.zones } : source.zones,
    morphs: cloneMorphs(source.morphs),
    width: source.width,
    height: source.height,
    thickness: source.thickness,
    x: pos.x,
    y: pos.y,
    z: pos.z,
    matrix: relativeMatrix.clone(),
    visible: true,
    seatStitchType: (source as any).seatStitchType,
    seatHeightCm: (source as any).seatHeightCm,
  } as any)

  await wait(80)

  const liveDuplicated = useAppStore.getState().items.get(newId)
  if (!isLinkedItem(liveDuplicated)) {
    store.select(newId, false)
    return newId
  }

  const duplicatedChildrenIds =
    liveDuplicated.childrenOrder?.map((el) => el.split('!')[1] as AnyID).filter(Boolean) ?? []

  const sourceChildren = sourceChildrenIds
    .map((id) => useAppStore.getState().items.get(id))
    .filter(isLinkedSon) as any[]

  // Emparejar por catalogID cuando sea posible; si no, por índice visual.
  const usedSourceIndexes = new Set<number>()
  duplicatedChildrenIds.forEach((dupChildId, index) => {
    const dupChild = useAppStore.getState().items.get(dupChildId)
    if (!isLinkedSon(dupChild)) return

    let sourceChild = sourceChildren.find(
      (child, i) =>
        !usedSourceIndexes.has(i) &&
        String(child.catalogID || '').toLowerCase() ===
          String(dupChild.catalogID || '').toLowerCase(),
    )
    if (sourceChild) {
      const matchedIndex = sourceChildren.indexOf(sourceChild)
      usedSourceIndexes.add(matchedIndex)
    } else {
      sourceChild = sourceChildren[index]
      if (sourceChild) usedSourceIndexes.add(index)
    }
    if (!sourceChild) return

    store.editItem(dupChildId, buildChildFinishPatch(sourceChild, liveDuplicated, newId) as any)
  })

  await wait(60)
  useAppStore.getState().setOBBScene?.()
  store.select(newId, false)

  return newId
}
